/// <reference types="@types/google.accounts" />
import { decodeJWT } from "aws-amplify/auth";

export default function () {
  const { login } = useLoginWithSocials();
  const route = useRoute();

  const {
    public: { googleClientId },
  } = useRuntimeConfig();
  const { authenticatedByOneTap } = useAnalytics();

  const onScriptLoaded = () => {
    if (!google) {
      // eslint-disable-next-line no-console
      console.warn("Google Accounts Client not loaded properly.");

      return;
    }

    const { onboardingEntry } = useOnboardingDialog();

    // initialise the google accounts client using google javascript api
    // https://developers.google.com/identity/gsi/web/reference/js-reference
    google.accounts.id.initialize({
      client_id: googleClientId,
      callback: async (oneTapResponse) => {
        const decodedJWT = decodeJWT(oneTapResponse.credential);
        const email = decodedJWT.payload.email;

        if (typeof email !== "string") {
          throw new Error("Google ID token does not contain an email.");
        }

        authenticatedByOneTap({
          source_path: route.path,
        });

        onboardingEntry.value = {
          type: OnboardingEntryType.OneTap,
          data: null,
        };

        // once the user has succesfully selected and verified their identity through onetap
        // take them to the sso flow with the selected email address as the loginHint (see: https://developers.google.com/identity/openid-connect/openid-connect#login-hint)
        login({
          provider: "Google",
          loginHint: email,
          redirectBackToCurrentPath: true,
        });
      },
    });

    // show the One Tap prompt
    google.accounts.id.prompt();
  };

  const { isLoggedIn } = useAuth();
  const platformStore = usePlatformStore();
  const isHomepage = useIsHomepage();

  const {
    result: shouldShowOneTapOnCategoryPage,
    loaded: isOneTapCategoryPageEnabled,
  } = useGrowthbook({
    feature: "account-creation-conversion-category-page-2",
    fallback: false,
    enabled: () => !getIsInAppBrowser(),
  });

  const {
    result: shouldShowOneTapOnEventPage,
    loaded: isOneTapEventPageEnabled,
  } = useGrowthbook({
    feature: "account-creation-conversion-event-page-2",
    fallback: false,
    enabled: () => !getIsInAppBrowser(),
  });

  const isGrowthbookLoaded = computed(
    () => isOneTapCategoryPageEnabled.value && isOneTapEventPageEnabled.value,
  );

  const BLACKLISTED_PATHS = computed(() => [
    "/login",
    "/register",
    "/forgot-password",
    "/password-reset",
    "/auth-redirect",
    ...(!shouldShowOneTapOnCategoryPage.value ? ["/category"] : []),
    ...(!shouldShowOneTapOnEventPage.value ? ["/event"] : []),
  ]);

  const isOneTapEnabled = computed(
    () =>
      platformStore.isMatchWornShirt &&
      !!googleClientId &&
      !BLACKLISTED_PATHS.value.some((path) => route.path.includes(path)) &&
      !isLoggedIn.value,
  );

  const scriptsToLoad = computed(() => {
    if (
      !isOneTapEnabled.value ||
      !isGrowthbookLoaded.value ||
      isHomepage.value
    ) {
      return [];
    }

    return [
      {
        src: "https://accounts.google.com/gsi/client",
        async: true,
        defer: true,
        onload: onScriptLoaded,
      },
    ];
  });

  useHead({
    script: scriptsToLoad,
  });
}
